import { useState, useEffect } from 'react';
import { Content, Header, Page } from '@backstage/core-components';
import { CircularProgress, Box, IconButton } from '@material-ui/core';
import Launch from '@material-ui/icons/Launch';
import { configApiRef, useApi, useRouteRefParams } from '@backstage/core-plugin-api';
import { githubWorkflowApiRef } from '../plugin';
import { WorkflowDispatchForm } from './WorkflowDispatchForm';
import { WorkflowRunStatusPage } from './WorkflowRunStatusPage';
import { WorkflowInfo } from '../types';
import { rootRouteRef } from '../routes';
import { Config } from '@backstage/config';

interface WorkflowConfig {
  owner: string;
  repo: string;
  workflowId: string;
}

export function getWorkflowFromConfig(config: Config, workflow: string): WorkflowConfig {
  const workflows = config.getOptionalConfig('github.workflows');

  if (!workflows) {
    throw new Error('No workflows configuration found in app-config.yaml');
  }

  const workflowConfig = workflows.getOptionalConfig(workflow);

  if (!workflowConfig) {
    throw new Error(`No workflow configuration found for workflow: ${workflow}`);
  }

  const owner = workflowConfig.getString('owner');
  const repo = workflowConfig.getString('repo');
  const workflowId = workflowConfig.getString('workflowId');

  return { owner, repo, workflowId };
}

export interface WorkflowDispatchPageProps {
  owner: string;
  repo: string;
  workflowId: string;
}

export const WorkflowDispatchPage = () => {
  const workflowPath = useRouteRefParams(rootRouteRef);
  const config = useApi(configApiRef);

  const { owner, repo, workflowId } = getWorkflowFromConfig(config, workflowPath.workflow);

  const api = useApi(githubWorkflowApiRef);
  const [workflowInfo, setWorkflowInfo] = useState<WorkflowInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [runId, setRunId] = useState<number | null>(null);

  // All hooks must be called before any conditional returns
  useEffect(() => {
    const fetchWorkflowInfo = async () => {
      try {
        setLoading(true);
        const workflow = await api.getWorkflow(owner, repo, workflowId);
        setWorkflowInfo(workflow);
      } catch (err) {
        setError(`Failed to fetch workflow information: ${err}`);
      } finally {
        setLoading(false);
      }
    };

    fetchWorkflowInfo();
  }, [api, owner, repo, workflowId]);

  // Helper function to generate GitHub workflow URL
  const getWorkflowUrl = (workflowFilePath?: string) => {
    const path = workflowFilePath || workflowId;
    // Link to the actual workflow file in the repository
    return `https://github.com/${owner}/${repo}/blob/main/${path}`;
  };

  // Helper component for GitHub link icon
  const GitHubWorkflowLink = ({ workflowPath: workflowFilePath }: { workflowPath?: string }) => (
    <IconButton
      component="a"
      href={getWorkflowUrl(workflowFilePath)}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: 'inherit', padding: 4 }}
      title="View workflow on GitHub"
    >
      <Launch />
    </IconButton>
  );

  // If we have a runId, show the status page
  if (runId && workflowInfo) {
    return (
      <WorkflowRunStatusPage
        owner={owner}
        repo={repo}
        runId={runId}
        workflowName={workflowInfo.name}
        onBack={() => setRunId(null)}
      />
    );
  }

  if (loading) {
    return (
      <Page themeId="tool">
        <Header
          title={
            <Box display="flex" alignItems="center">
              Loading Workflow...
              <Box ml={1}>
                <GitHubWorkflowLink />
              </Box>
            </Box>
          }
          subtitle="Fetching workflow information"
        />
        <Content>
          <Box display="flex" justifyContent="center" alignItems="center" height={200}>
            <CircularProgress />
          </Box>
        </Content>
      </Page>
    );
  }

  if (error) {
    return (
      <Page themeId="tool">
        <Header
          title={
            <Box display="flex" alignItems="center">
              Error
              <Box ml={1}>
                <GitHubWorkflowLink />
              </Box>
            </Box>
          }
          subtitle={error}
        />
        <Content>
          <Box p={3}>
            <p>Unable to load workflow information. Please check the workflow ID and try again.</p>
          </Box>
        </Content>
      </Page>
    );
  }

  const workflowName = workflowInfo?.name || 'workflowId';

  return (
    <Page themeId="tool">
      <Header
        title={
          <Box display="flex" alignItems="center">
            {workflowName}
            <Box ml={1}>
              <GitHubWorkflowLink workflowPath={workflowInfo?.path} />
            </Box>
          </Box>
        }
      />
      <Content>
        <WorkflowDispatchForm owner={owner} repo={repo} workflowId={workflowId} onSuccess={setRunId} />
      </Content>
    </Page>
  );
};
